/**
 * Studio Template Hub - Template Definitions
 *
 * Hardcoded templates for the three sections:
 * - Content Editor (Video/Image)
 * - Document Editor (Docs/PDF)
 * - TwinBoost AI Video (Avatar/AI)
 */

// ============================================================================
// Types
// ============================================================================

export type PresetKey = '1080x1080' | '1080x1920' | '1280x720' | '1920x1080';

export type ContentTemplate = {
  id: string;
  name: string;
  badge: string;
  thumbnail?: string;
  preset: PresetKey;
  description?: string;
  gradient: string; // Fallback gradient for thumbnail
};

export type DocumentTemplate = {
  id: string;
  name: string;
  badge: string;
  thumbnail?: string;
  pageSize: string;
  orientation: 'portrait' | 'landscape';
  description?: string;
  gradient: string;
};

export type TemplateSlot = {
  id: string;
  label: string;
  placeholder: string;
  defaultValue?: string;
};

export type TwinBoostTemplate = {
  id: string;
  name: string;
  badge: string;
  thumbnail?: string;
  tone: string;
  duration: string;
  scriptTemplate: string;
  slots: TemplateSlot[];
  description?: string;
  gradient: string;
};

// ============================================================================
// Content Editor Templates (Video/Image)
// ============================================================================

export const CONTENT_TEMPLATES: ContentTemplate[] = [
  {
    id: 'yt-intro',
    name: 'YouTube Intro',
    badge: 'Video',
    preset: '1920x1080',
    description: 'Eye-catching intro for your YouTube videos',
    gradient: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
  },
  {
    id: 'ig-story',
    name: 'Instagram Story',
    badge: 'Story',
    preset: '1080x1920',
    description: 'Vertical format perfect for Instagram Stories',
    gradient: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
  },
  {
    id: 'tiktok',
    name: 'TikTok/Shorts',
    badge: 'Shorts',
    preset: '1080x1920',
    description: 'Vertical video for TikTok and YouTube Shorts',
    gradient: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
  },
  {
    id: 'thumbnail',
    name: 'Thumbnail',
    badge: 'Image',
    preset: '1280x720',
    description: 'Click-worthy thumbnails for your videos',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    id: 'presentation',
    name: 'Presentation',
    badge: 'Slides',
    preset: '1920x1080',
    description: 'Professional slides and presentations',
    gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  },
];

// ============================================================================
// Document Editor Templates (Docs/PDF)
// ============================================================================

export const DOCUMENT_TEMPLATES: DocumentTemplate[] = [
  {
    id: 'resume',
    name: 'Resume',
    badge: 'Resume',
    pageSize: 'letter',
    orientation: 'portrait',
    description: 'Professional resume template',
    gradient: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
  },
  {
    id: 'flyer',
    name: 'Flyer',
    badge: 'Print',
    pageSize: 'letter',
    orientation: 'portrait',
    description: 'Promotional flyers and handouts',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    id: 'poster',
    name: 'Poster',
    badge: 'Print',
    pageSize: 'tabloid',
    orientation: 'portrait',
    description: 'Large format posters',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    id: 'business-card',
    name: 'Business Card',
    badge: 'Card',
    pageSize: 'custom',
    orientation: 'landscape',
    description: 'Standard business card design',
    gradient: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
  },
  {
    id: 'social-post',
    name: 'Social Post',
    badge: 'Social',
    pageSize: 'square',
    orientation: 'portrait',
    description: 'Square posts for social media',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
];

// ============================================================================
// TwinBoost AI Video Templates
// ============================================================================

export const TWINBOOST_TEMPLATES: TwinBoostTemplate[] = [
  {
    id: 'launch-announcement',
    name: 'Product Launch',
    badge: 'AI Video',
    tone: 'Confident',
    duration: '45-60s',
    description: 'Bold intro, clear benefits, confident CTA',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    scriptTemplate:
      '{{hook}}\n\nHi, I am {{name}}. Today we are launching {{product}}. {{value}}.\n\n{{proof}}\n\n{{cta}}',
    slots: [
      { id: 'hook', label: 'Hook', placeholder: 'Open with a bold statement', defaultValue: 'This changes everything.' },
      { id: 'name', label: 'Speaker name', placeholder: 'Your name', defaultValue: 'Sam' },
      { id: 'product', label: 'Product', placeholder: 'Product name', defaultValue: 'TwinBoost' },
      { id: 'value', label: 'Value prop', placeholder: 'Main benefit', defaultValue: 'It turns your ideas into high-quality videos in minutes.' },
      { id: 'proof', label: 'Proof', placeholder: 'Credibility or result', defaultValue: 'Our creators are already shipping 5x more content.' },
      { id: 'cta', label: 'CTA', placeholder: 'What to do next', defaultValue: 'Start your first render today.' },
    ],
  },
  {
    id: 'founder-story',
    name: 'Founder Story',
    badge: 'AI Video',
    tone: 'Warm',
    duration: '60-75s',
    description: 'Personal origin story, mission-driven close',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    scriptTemplate:
      '{{hook}}\n\nI built {{product}} because {{problem}}.\n\n{{journey}}\n\n{{cta}}',
    slots: [
      { id: 'hook', label: 'Hook', placeholder: 'What sparked this?', defaultValue: 'It started with a simple frustration.' },
      { id: 'product', label: 'Product', placeholder: 'Product name', defaultValue: 'TwinBoost' },
      { id: 'problem', label: 'Problem', placeholder: 'Problem you solved', defaultValue: 'creators were spending hours on repetitive video work.' },
      { id: 'journey', label: 'Journey', placeholder: 'What changed', defaultValue: 'We built a system that captures your voice, your face, and your style.' },
      { id: 'cta', label: 'CTA', placeholder: 'Invite people in', defaultValue: 'Join the first wave and build with us.' },
    ],
  },
  {
    id: 'social-proof',
    name: 'Social Proof',
    badge: 'AI Video',
    tone: 'Direct',
    duration: '30-45s',
    description: 'Results-driven, metrics first',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    scriptTemplate:
      '{{hook}}\n\n{{metric}}\n\n{{value}}\n\n{{cta}}',
    slots: [
      { id: 'hook', label: 'Hook', placeholder: 'Grab attention quickly', defaultValue: 'Here is the real impact.' },
      { id: 'metric', label: 'Metric', placeholder: 'Key result', defaultValue: 'Creators are publishing 4x more video weekly.' },
      { id: 'value', label: 'Value prop', placeholder: 'Why it matters', defaultValue: 'Your audience grows because you show up consistently.' },
      { id: 'cta', label: 'CTA', placeholder: 'Next step', defaultValue: 'Book a demo to see it live.' },
    ],
  },
  {
    id: 'tutorial',
    name: 'Tutorial',
    badge: 'AI Video',
    tone: 'Friendly',
    duration: '60-90s',
    description: 'Step-by-step explainer with clear instructions',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    scriptTemplate:
      '{{intro}}\n\nStep 1: {{step1}}\n\nStep 2: {{step2}}\n\nStep 3: {{step3}}\n\n{{outro}}',
    slots: [
      { id: 'intro', label: 'Introduction', placeholder: 'What are we learning?', defaultValue: 'In this tutorial, I will show you how to create your first AI video.' },
      { id: 'step1', label: 'Step 1', placeholder: 'First step', defaultValue: 'Upload a short video of yourself speaking naturally.' },
      { id: 'step2', label: 'Step 2', placeholder: 'Second step', defaultValue: 'Write or paste your script into the editor.' },
      { id: 'step3', label: 'Step 3', placeholder: 'Third step', defaultValue: 'Click render and watch your AI twin come to life.' },
      { id: 'outro', label: 'Outro', placeholder: 'Wrap it up', defaultValue: 'That is it! You are ready to scale your content.' },
    ],
  },
];
